<script lang="ts">
	// Services
	import { format } from 'svelte-i18n';
	// Svelte
	import { createEventDispatcher } from 'svelte';
	// Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	// Design System
	import { Text, CardGrid, SelectCard, Token, Input } from '@Shelly/shelly-design-system';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { getCameraActionZones } from 'src/components/Scenes/helpers/device_actions/custom_cases/camera_actions';
	import {
		CAMERA_ACTION_NAMES,
		CAMERA_RECORD_DURATION,
	} from 'src/components/Scenes/helpers/camera_action_helpers';
	import { getCameraSoundOptions } from 'src/components/Camera/helpers/camera-sounds';
	import { getCameraZoneName } from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';

	export let active_actions: any[] = [];
	export let sceneIsValid; // Do not remove: used by the parent for validation.
	export let deviceId: string = '';

	type CameraValueOption = { id: string; title: string; icon: string };

	const dispatch = createEventDispatcher();

	const cardBg = () => ($showNewLayout ? 'color-1' : undefined);

	/**
	 * Pick the value cards for the active category.
	 */
	const getValueOptions = (
		category: string,
		privacy: CameraValueOption[],
		arm: CameraValueOption[],
		sounds: CameraValueOption[],
	): CameraValueOption[] => {
		if (category === CAMERA_ACTION_NAMES.PRIVACY) {
			return privacy;
		}
		if (category === CAMERA_ACTION_NAMES.ARM) {
			return arm;
		}
		if (category === CAMERA_ACTION_NAMES.PLAY_SOUND) {
			return sounds;
		}
		return [];
	};

	let selectedCategory = '';
	let selectedValue = '';
	let selectedZone = '';
	let duration: number | '' = '';

	$: durationError =
		duration !== '' &&
		(Number(duration) < CAMERA_RECORD_DURATION.MIN || Number(duration) > CAMERA_RECORD_DURATION.MAX)
			? $format('scene_camera_duration_range', {
					values: { min: CAMERA_RECORD_DURATION.MIN, max: CAMERA_RECORD_DURATION.MAX },
					default: `Enter a value between ${CAMERA_RECORD_DURATION.MIN} and ${CAMERA_RECORD_DURATION.MAX}`,
				})
			: undefined;

	$: zones = getCameraActionZones(
		deviceId,
		$devices_settings as Record<string, DeviceSettingsG2 | undefined>,
	);

	$: categories = [
		{
			id: CAMERA_ACTION_NAMES.RECORD,
			title: $format('local_action_camera_start_recording', { default: 'Start Recording' }),
			icon: 'fas fa-circle-dot',
		},
		{
			id: CAMERA_ACTION_NAMES.CAPTURE,
			title: $format('local_action_camera_capture_image', { default: 'Capture Image' }),
			icon: 'fas fa-camera',
		},
		{
			id: CAMERA_ACTION_NAMES.PRIVACY,
			title: $format('camera_privacy_mode', { default: 'Privacy Mode' }),
			icon: 'fas fa-video-slash',
		},
		{
			id: CAMERA_ACTION_NAMES.ARM,
			title: $format('camera_arm_disarm', { default: 'Arm / Disarm' }),
			icon: 'fas fa-shield',
		},
		...(zones.length > 0
			? [
					{
						id: CAMERA_ACTION_NAMES.ZONE,
						title: $format('scene_camera_action_zone', { default: 'Camera zone' }),
						icon: 'fas fa-vector-square',
					},
				]
			: []),
		{
			id: CAMERA_ACTION_NAMES.PLAY_SOUND,
			title: $format('scene_camera_action_play_sound', { default: 'Play sound' }),
			icon: 'fas fa-volume-high',
		},
	];

	$: privacyValues = [
		{
			id: 'true',
			title: $format('camera_privacy_mode_on', { default: 'Privacy On' }),
			icon: 'fas fa-video-slash',
		},
		{
			id: 'false',
			title: $format('camera_privacy_mode_off', { default: 'Privacy Off' }),
			icon: 'fas fa-video',
		},
	];

	$: armValues = [
		{
			id: 'true',
			title: $format('camera_motion_detection_armed', { default: 'Armed' }),
			icon: 'fas fa-shield',
		},
		{
			id: 'false',
			title: $format('camera_motion_detection_disarmed', { default: 'Disarmed' }),
			icon: 'fas fa-shield-halved',
		},
	];

	$: zoneValues = [
		{
			id: 'true',
			title: $format('camera_zone_enable', { default: 'Enabled' }),
			icon: 'fa-duotone fa-light fa-toggle-on',
		},
		{
			id: 'false',
			title: $format('camera_zone_disable', { default: 'Disabled' }),
			icon: 'fa fa-toggle-off',
		},
	];

	const soundValues = getCameraSoundOptions();

	$: valueOptions = getValueOptions(selectedCategory, privacyValues, armValues, soundValues);

	$: selectedZoneName = selectedZone
		? getCameraZoneName(selectedZone, $devices_settings[deviceId?.split('_')[0]])
		: '';

	$: stateHeadings = {
		[CAMERA_ACTION_NAMES.ARM]: $format('scene_camera_select_arm', {
			default: 'Arm or disarm camera',
		}),
		[CAMERA_ACTION_NAMES.PRIVACY]: $format('scene_camera_select_privacy', {
			default: 'Set privacy mode',
		}),
		[CAMERA_ACTION_NAMES.PLAY_SOUND]: $format('scene_camera_select_sound', {
			default: 'Select sound',
		}),
	};

	$: stateHeading = stateHeadings[selectedCategory] ?? '';

	/**
	 * Seed the local UI state from the action being edited.
	 */
	const restore = () => {
		const active = active_actions?.[0];
		if (!active?.name) {
			return;
		}
		selectedCategory = active.name;
		switch (active.name) {
			case CAMERA_ACTION_NAMES.PRIVACY:
			case CAMERA_ACTION_NAMES.ARM:
			case CAMERA_ACTION_NAMES.PLAY_SOUND:
				selectedValue = String(active.value ?? '');
				break;
			case CAMERA_ACTION_NAMES.RECORD:
				duration = active.duration != null ? Number(active.duration) : '';
				break;
			case CAMERA_ACTION_NAMES.ZONE:
				selectedZone = String(active.add_info_ind ?? active.addOnInd ?? '');
				selectedValue = String(active.value ?? '');
				break;
			default:
				break;
		}
	};

	const buildActiveAction = () => {
		switch (selectedCategory) {
			case CAMERA_ACTION_NAMES.CAPTURE:
				return { name: selectedCategory, value: 'capture' };
			case CAMERA_ACTION_NAMES.RECORD: {
				const action: any = { name: selectedCategory, value: 'record' };
				if (duration !== '' && Number(duration) > 0) {
					action.duration = Number(duration);
				}
				return action;
			}
			case CAMERA_ACTION_NAMES.PRIVACY:
			case CAMERA_ACTION_NAMES.ARM:
			case CAMERA_ACTION_NAMES.PLAY_SOUND:
				return selectedValue ? { name: selectedCategory, value: selectedValue } : null;
			case CAMERA_ACTION_NAMES.ZONE:
				return selectedZone && selectedValue
					? { name: selectedCategory, value: selectedValue, add_info_ind: selectedZone }
					: null;
			default:
				return null;
		}
	};

	const emit = () => {
		const action = buildActiveAction();
		active_actions = action ? [action] : [];
		dispatch('change_active', { new_active: [...active_actions] });
	};

	const onSelectCategory = (id: string) => {
		selectedCategory = id;
		selectedValue = '';
		selectedZone = '';
		duration = '';
		emit();
	};

	const onSelectValue = (id: string) => {
		selectedValue = id;
		emit();
	};

	const onSelectZone = (id: string) => {
		selectedZone = id;
		selectedValue = '';
		emit();
	};

	const clampDuration = (value: number): number =>
		Math.min(Math.max(Math.floor(value), CAMERA_RECORD_DURATION.MIN), CAMERA_RECORD_DURATION.MAX);

	const onDurationInput = (event: Event) => {
		const raw = (event.target as HTMLInputElement)?.value ?? '';
		if (raw === '') {
			duration = '';
		} else {
			const parsed = Number(raw);
			duration = Number.isNaN(parsed) ? '' : clampDuration(parsed);
		}
		emit();
	};

	restore();
</script>

<Text element="h2" class="mb-s">
	{capFirstChar($format('label_select_action', { default: 'Select action' }))}
</Text>
<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
	{#each categories as card}
		<div class="d-flex">
			<SelectCard
				cardBgColor={cardBg()}
				class="width-full"
				type="horizontal"
				variant="radio"
				title={capFirstChar(card.title)}
				icon={card.icon}
				selected={selectedCategory === card.id}
				on:click={() => onSelectCategory(card.id)}
			/>
		</div>
	{/each}
</CardGrid>

{#if selectedCategory === CAMERA_ACTION_NAMES.RECORD}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('scene_camera_record_duration', { default: 'Recording duration (optional)' })}
		</Text>
		<div class="d-flex flex-wrap align-items-center gap-s">
			<Input
				{...getNumberInputConfig(true)}
				class="width-md-s width-sm-full"
				size="m"
				icon="fas fa-timer"
				min={CAMERA_RECORD_DURATION.MIN}
				max={CAMERA_RECORD_DURATION.MAX}
				error={durationError}
				label={`${CAMERA_RECORD_DURATION.DEFAULT}`}
				value={duration === '' ? '' : `${duration}`}
				aria-label={$format('scene_camera_record_duration', {
					default: 'Recording duration (optional)',
				})}
				id="input-camera-duration"
				on:input={onDurationInput}
			/>
			<Text>
				{capFirstChar($format('select_units_seconds', { default: 'Seconds' }))}
			</Text>
		</div>
	</div>
{/if}

{#if selectedCategory === CAMERA_ACTION_NAMES.ZONE && zones.length > 0}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{$format('select_zone', { default: 'Select Zone' })}
		</Text>
		<div class="d-flex flex-wrap mb-l gap-s">
			{#each zones as zone}
				<Token
					id="token-camera-action-zone-{zone.id}"
					title={capFirstChar(zone.title)}
					icon={zone.icon}
					size="m"
					selected={selectedZone === zone.id}
					on:click={() => onSelectZone(zone.id)}
				/>
			{/each}
		</div>

		{#if selectedZone}
			<Text element="h2" class="mb-s">
				{capFirstChar(
					$format('scene_camera_select_zone_state', {
						values: { zone: selectedZoneName },
						default: `Change zone ${selectedZoneName} to`,
					}),
				)}
			</Text>
			<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
				{#each zoneValues as card}
					<div class="d-flex">
						<SelectCard
							cardBgColor={cardBg()}
							class="width-full"
							type="horizontal"
							variant="radio"
							title={capFirstChar(card.title)}
							icon={card.icon}
							selected={selectedValue === card.id}
							on:click={() => onSelectValue(card.id)}
						/>
					</div>
				{/each}
			</CardGrid>
		{/if}
	</div>
{/if}

{#if selectedCategory === CAMERA_ACTION_NAMES.PRIVACY || selectedCategory === CAMERA_ACTION_NAMES.ARM || selectedCategory === CAMERA_ACTION_NAMES.PLAY_SOUND}
	<div class="mt-m">
		<Text element="h2" class="mb-s">
			{capFirstChar(stateHeading)}
		</Text>
		<CardGrid sm={200} md={200} xl={200} noPaddingBottom>
			{#each valueOptions as card}
				<div class="d-flex">
					<SelectCard
						cardBgColor={cardBg()}
						class="width-full"
						type="horizontal"
						variant="radio"
						title={capFirstChar(card.title)}
						icon={card.icon}
						selected={selectedValue === card.id}
						on:click={() => onSelectValue(card.id)}
					/>
				</div>
			{/each}
		</CardGrid>
	</div>
{/if}
